import type { AnyRouter } from '@tanstack/react-router'

/**
 * Page transitions (BR-11): the View Transitions API, where the browser has it, so the page you leave
 * can go out as the next comes in (the styles are in tokens.css, under motion). A popup closing onto
 * the map and the grid closing sink and fade away, one page in a popup gives way to the next, and the
 * screens before the map crossfade. A popup or the grid opening needs none: its own entrance does it,
 * and so does a tab (the address only changes its search).
 *
 * Never holds up a navigation: the browser stops drawing the page while the change is made, so it
 * waits for the new page at most this long, and taps go straight through while it animates.
 */
const WAIT_MS = 200

const onMap = (path: string) => path === '/map' || path === '/grid'

export function pageTransitions(router: AnyRouter) {
  if (typeof document.startViewTransition !== 'function') return
  const root = document.documentElement
  root.classList.add('fm-vt')
  let at = { path: location.pathname, index: 0 }
  router.subscribe('onResolved', ({ toLocation }) => {
    at = { path: toLocation.pathname, index: toLocation.state.__TSR_index ?? 0 }
  })
  let runs = 0
  router.startViewTransition = (fn: () => Promise<void>) => {
    const to = router.latestLocation
    const kind = kindOf(at.path, to.pathname, (to.state.__TSR_index ?? 0) < at.index)
    if (!kind || document.visibilityState !== 'visible') return fn()
    const run = ++runs
    root.dataset.vt = kind
    let done: Promise<void> | undefined
    const go = () => {
      done ??= fn()
      return done
    }
    const wait = () => new Promise<void>((r) => window.setTimeout(r, WAIT_MS))
    const vt = document.startViewTransition(() => Promise.race([go(), wait()]))
    const clear = () => {
      if (run === runs) delete root.dataset.vt
    }
    vt.finished.then(clear, clear)
    // A transition skipped (by a newer one, or below) is nothing to report.
    vt.ready.catch(() => {})
    // The browser makes the change only once it has drawn the page as it was, which a busy or
    // throttled page may not do for a while: then the page changes anyway, without the animation.
    return Promise.race([vt.updateCallbackDone.catch(() => {}), wait()]).then(() => {
      if (!done) vt.skipTransition()
      return go()
    })
  }
}

/** Which way the page changes (data-vt, read by tokens.css), or nothing to animate. */
function kindOf(from: string, to: string, back: boolean): 'screen' | 'close' | 'page' | 'back' | null {
  if (from === to) return null
  if (!document.querySelector('.fm-mapscreen')) return 'screen'
  if (onMap(to)) return onMap(from) && to === '/grid' ? null : 'close'
  if (onMap(from)) return null
  return back ? 'back' : 'page'
}
